import { keyBy } from "lodash";
import { combineReducers } from "redux";

import {
  SITES_FETCH_ALL_PENDING,
  SITES_FETCH_ALL_FULFILLED,
  SITES_FETCH_ALL_REJECTED,
  SITES_FETCH_FLIGHT_PLAN_FULFILLED,
} from "./constants";
import { Err, GeoJSON, ReduxAction } from "../../../typings";

export type FlightPlan = {
  customerId: string;
  siteId: string;
  siteKmlUrl: string;
  fixedWingKmlUrl: string;
  quadcopterKmlUrl: string;
};

/*
 @deprecated
 This type doesn't represent server response
 Use ISite type from src/apiFacades/csu/site.ts
 */
export type Site = {
  id: string;
  name: string;
  customer: string;
  updatedAt: string;
  boundary?: GeoJSON;
};

export type SitesState = {
  pending: boolean;
  error: Err | null;
  entries: { [key: string]: Site };
  flightPlans: { [key: string]: FlightPlan };
};

function entries(
  state: { [key: string]: Site } = {},
  action: ReduxAction<any>,
): { [key: string]: Site } {
  switch (action.type) {
    case SITES_FETCH_ALL_PENDING:
      return state;
    case SITES_FETCH_ALL_FULFILLED:
      return keyBy(action.payload, ({ id, customer }) =>
        createCompositeId([customer, id]),
      );
    case SITES_FETCH_ALL_REJECTED:
      return {};
    default:
      return state;
  }
}

function flightPlans(
  state: { [key: string]: FlightPlan } = {},
  action: ReduxAction<any>,
): { [key: string]: FlightPlan } {
  switch (action.type) {
    case SITES_FETCH_FLIGHT_PLAN_FULFILLED:
      return {
        ...state,
        [createCompositeId([
          action.payload.customerId,
          action.payload.siteId,
        ])]: action.payload,
      };
    default:
      return state;
  }
}

function error(state: Err | null = null, action: ReduxAction<any>): Err | null {
  switch (action.type) {
    case SITES_FETCH_ALL_PENDING:
    case SITES_FETCH_ALL_FULFILLED:
      return null;
    case SITES_FETCH_ALL_REJECTED:
      return action.payload;
    default:
      return state;
  }
}

function pending(state = false, action: ReduxAction<any>): boolean {
  switch (action.type) {
    case SITES_FETCH_ALL_PENDING:
      return true;
    case SITES_FETCH_ALL_FULFILLED:
    case SITES_FETCH_ALL_REJECTED:
      return false;
    default:
      return state;
  }
}

export default combineReducers({ pending, error, entries, flightPlans });

export function getSiteById(
  state: SitesState,
  customerId: string,
  siteId: string,
): Site {
  return state.entries[createCompositeId([customerId, siteId])] || null;
}

export function getFlightPlanById(
  state: SitesState,
  customerId: string,
  siteId: string,
): FlightPlan {
  return state.flightPlans[createCompositeId([customerId, siteId])] || null;
}

export function getSitesList(state: SitesState, customerId?: string): Site[] {
  const sitesDict = state.entries;
  if (!sitesDict) {
    return [];
  }
  const sitesArray = Object.keys(sitesDict).map(key => sitesDict[key]);
  if (!customerId) {
    return sitesArray;
  }
  return sitesArray.filter(({ customer }) => customer === customerId);
}

export function createCompositeId(arrKeys: string[]): string {
  return arrKeys.reduce((acc, key) => `${acc}/${key}`);
}

export function createCompositeSiteId(site: Site): string {
  return createCompositeId([site.customer, site.id]);
}
